import  { useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import aptitudeLogo from "./../images/aptitude-logo.svg";
import useMediaQuery from '@mui/material/useMediaQuery';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { connect } from 'react-redux';
import { loginUser } from '../redux/actions/userAction';
import { useMutation } from 'react-query';
import { sendEmailVerification } from '../queries/userQuery';
import {Helmet} from 'react-helmet';

const styles = (theme) => ({
    ...theme.customStyle,
    image: {
        margin: '3vh auto 5vh auto',
        height: '30vh',
        minHeight: 100,
        maxHeight: 480,
    },
    form: {
        textAlign: 'center',
    },
    textField: {
        margin: '10px auto 10px auto',
    },
    button: {
        marginTop: 20,
        position: 'relative',
        marginBottom: 15,
    },
    customError: {
        color: 'red',
        fontSize: '0.8rem',
        marginTop: '10px',
    },
    progress: {
        position: 'absolute',
    },
});

const SubmitButton = ({ submit, children, loading , type , disabled }) => (
    <Button
        type={type ? type : 'submit'}
        variant="contained"
        color="primary"
        style={{
            marginTop: 20,
            width: '100%',
            position: 'relative',
            marginBottom: 15,
            textTransform: 'none',
        }}
        disabled={loading || disabled}
        onClick={submit}>
            {children}
            {loading && (
                <CircularProgress size={30} style={{ position: 'absolute' }} />
            )}
    </Button>
);

const UserEmailPasswordForm = ({ classes, errors, handleSubmit, requireMFAToken, loading }) => {
    const [cr, setCr] = useState({ email: '', password: '', token: '' });
    const handleChange = (event) => {
        setCr((s) => ({ ...s, [event.target.name]: event.target.value }));
    };

    // keep track whether user have submitted the 2FA token
    const [tFSubmit,setTFSubmit] = useState(false);

    const submit = () => handleSubmit(cr);
    const tokenError = tFSubmit && !loading;
    const sendEmailVerificationMutation = useMutation(sendEmailVerification);
    const handleVerification = () => {
        sendEmailVerificationMutation.mutate({email:cr.email});
    };

    return <>
        {requireMFAToken ? (
            <form noValidate onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                <Typography style={{ textAlign: "left" }} variant="body2">
                    Your account is protected with two-factor authentication. 
                    We've sent you a text message. Please enter the code below.
                </Typography>
                <TextField 
                    variant="standard"
                    label="Two-Factor Code"
                    color="primary"
                    name="token"
                    id="token"
                    inputProps={{ pattern: '[0-9]*', inputMode: 'numeric' }}
                    autoComplete="one=time-code"
                    style={{ width: '100%', margin: 5 }}
                    value={cr.token}
                    onChange={(e)=>{handleChange(e);setTFSubmit(false)}}
                    disabled={loading}
                    error={tokenError}
                    helperText={tokenError ? "Incorrect 2FA code. Try re-entering the code." : "" } />
                <SubmitButton submit={(e) => { setTFSubmit(true); submit(); }} loading={loading}>
                    Continue
                </SubmitButton>
                <Button color="primary" style={{ textTransform: "none" }} onClick={()=> {
                    setTFSubmit(false);
                    setCr({ ...cr, token: ''});
                    handleSubmit({ ...cr, reSendToken: true }); }}>
                        Resend 2FA Code
                </Button>
            </form>
        ) : (
            <form noValidate onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                <TextField
                    variant="standard"
                    id="email"
                    name="email"
                    type="email"
                    label="Email"
                    className={classes.textField}
                    autoComplete="email"
                    helperText={errors && errors.email}
                    error={errors && errors.email ? true : false}
                    value={cr.email}
                    onChange={handleChange}
                    fullWidth />
                <TextField
                    variant="standard"
                    id="password"
                    name="password"
                    type="password"
                    label="Password"
                    className={classes.textField}
                    autoComplete="password"
                    helperText={errors && errors.password}
                    error={errors && errors.password ? true : false}
                    value={cr.password}
                    onChange={handleChange}
                    fullWidth />
                {errors && errors.error && (
                    <Typography
                        variant="body2"
                        className={classes.customError}>
                            {errors.error}
                    </Typography>
                )}
                {
                    (errors && errors.reason === "email-verification") ? (
                        <>
                            <SubmitButton submit={handleVerification} loading={sendEmailVerificationMutation.isLoading} type="button" disabled={sendEmailVerificationMutation.isSuccess}>
                                Resend Email Verification
                            </SubmitButton>
                            {
                                sendEmailVerificationMutation.isSuccess && <Typography variant="subtitle2" color="primary">The verification email is sent to your email address. Please check your spam folder if you don't find it in your inbox.</Typography>
                            }
                            {
                                sendEmailVerificationMutation.isError && <Typography variant="subtitle2" color="error">An error occured while processing your request. Please refresh the page and try again. Please contact us if the error persists.</Typography>
                            }
                        </>
                    ) : (
                        <>
                            <SubmitButton submit={submit} loading={loading}>
                                LOGIN
                            </SubmitButton>
                            <Typography variant="subtitle2">
                                <a href="/forgetpassword" style={{ color: "inherit" }}>
                                    Forget password?
                                </a>
                            </Typography>
                        </>
                    )
                }
            </form>
        )}
    </>
}

function Login(props) {
    const handleSubmit = (userData) => {
        props.loginUser(userData, props.history, props?.location?.state?.nextUrl || '/portal');
    };

    const {
        classes,
        UI: { errors },
        user: { loading, requireMFAToken },
    } = props;

    const isMobile = useMediaQuery('(max-width:600px)');

    return (
        <div className="container">
            <Helmet>
                <title>Login</title>
            </Helmet>
            <Grid container className={classes.form}>
                <Grid item xs={12} style={{ marginBottom: 25 }}>
                    <img
                        src={aptitudeLogo}
                        alt="icon"
                        className={classes.image}
                        style={{ height: isMobile ? 100 : "100px" }} />
                </Grid>
                <Grid item xs={1} md={3} lg={4} />
                <Grid item xs={10} md={6} lg={4}>
                    <UserEmailPasswordForm
                        loading={loading}
                        classes={classes}
                        errors={errors}
                        requireMFAToken={requireMFAToken}
                        handleSubmit={handleSubmit} />
                    <br />
                </Grid>
                <Grid item xs={1} md={3} lg={4} />
            </Grid>
        </div>
    );
}


const mapStateToProps = (state) => ({
    user: state.user,
    UI: state.UI,
});

const mapDispatchToProps = { loginUser };

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(Login));
